import React, { useState, useEffect, useRef } from 'react';
import { placeOrder, createRazorpayOrder, verifyRazorpayPayment, loadRazorpayScript, initiatePayuPayment, redirectToPayu, getAddresses, addAddress, updateAddress, updateProfile, syncCart, guestSyncCart, getGuestContact, saveGuestContact, clearGuestContact } from '../lib/api';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import LocationPickerModal, { buildQuery, geocodeAddress, waitForGoogleMaps } from '../components/LocationPickerModal';
import MapPreview from '../components/MapPreview';
import { trackEvent } from '../lib/metaPixel';
import './CheckoutPage.css';

// sessionStorage key used to snapshot the checkout form before redirecting to
// a hosted payment page (PayU), so it can be restored if the customer is
// bounced back after a cancelled/failed payment. Cleared on read.
const CHECKOUT_DRAFT_KEY = 'nq_checkout_draft';

const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Chandigarh','Delhi','Jammu & Kashmir','Ladakh',
  'Puducherry',
];

import { getPublicCoupons, validateCoupon, getShippingSettings, getPaymentSettings } from '../lib/api';
import { computeShipping, codEligibility } from '../lib/shipping';

function ShieldIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

/* ─── Coupon Popup — fetches real coupons from DB ──────────── */
function CouponModal({ open, onClose, onApply, appliedCode, total }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getPublicCoupons()
      .then(data => setCoupons(Array.isArray(data) ? data : []))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;
  return (
    <div className="coupon-overlay" onClick={onClose}>
      <div className="coupon-modal" onClick={e => e.stopPropagation()}>
        <div className="coupon-modal__header">
          <h3 className="coupon-modal__title">Available Coupons</h3>
          <button className="coupon-modal__close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="coupon-modal__list">
          {loading && <p className="coupon-modal__empty">Loading coupons…</p>}
          {!loading && coupons.length === 0 && (
            <p className="coupon-modal__empty">No coupons available right now.</p>
          )}
          {!loading && coupons.map(c => {
            const isActive = appliedCode === c.code;
            const eligible = total >= (c.minOrder || 0);
            return (
              <div key={c.code} className={`coupon-card${isActive ? ' coupon-card--active' : ''}${!eligible ? ' coupon-card--disabled' : ''}`}>
                <div className="coupon-card__left">
                  <div className="coupon-card__icon"><TagIcon /></div>
                  <div className="coupon-card__body">
                    <div className="coupon-card__code">{c.code}</div>
                    <div className="coupon-card__label">{c.label}</div>
                    {c.desc && <div className="coupon-card__desc">{c.desc}</div>}
                    {c.minOrder > 0 && (
                      <div className="coupon-card__min">
                        {eligible ? '✓ Eligible on your order' : `Min. order ₹${c.minOrder}`}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  className={`coupon-card__btn${isActive ? ' coupon-card__btn--applied' : ''}${!eligible ? ' coupon-card__btn--locked' : ''}`}
                  disabled={!eligible}
                  onClick={() => { if (eligible) { onApply(c.code); onClose(); } }}
                >
                  {isActive ? 'Applied ✓' : eligible ? 'Apply' : 'Locked'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Promo Row (reusable for desktop right panel & mobile) ── */
function PromoSection({ promoCode, setPromoCode, handleApplyPromo, promoError, promoSuccess, appliedPromo, total, onRemove, applying }) {
  const [couponModalOpen, setCouponModalOpen] = useState(false);

  const handleApplyFromModal = (code) => {
    setPromoCode(code);
    handleApplyPromo(code);
  };

  return (
    <>
      {!appliedPromo ? (
        <>
          <div className="co-promo">
            <input
              className="co-promo__input"
              type="text"
              placeholder="Discount code or gift card"
              value={promoCode}
              onChange={e => { setPromoCode(e.target.value.toUpperCase()); }}
              onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
            />
            <button className="co-promo__btn" type="button" onClick={() => handleApplyPromo()} disabled={applying || !promoCode.trim()}>
              {applying ? '…' : 'Apply'}
            </button>
          </div>
          <div className="co-promo__actions">
            <button className="co-promo__view-all" type="button" onClick={() => setCouponModalOpen(true)}>
              <TagIcon /> View all coupons
            </button>
          </div>
        </>
      ) : (
        <div className="co-promo__actions">
          <button className="co-promo__view-all" type="button" onClick={() => setCouponModalOpen(true)}>
            <TagIcon /> View all coupons
          </button>
          <button className="co-promo__remove" type="button" onClick={onRemove}>
            Remove
          </button>
        </div>
      )}
      {promoError   && <p className="co-promo__msg co-promo__msg--err">{promoError}</p>}
      {promoSuccess && <p className="co-promo__msg co-promo__msg--ok">✓ {promoSuccess}</p>}

      <CouponModal
        open={couponModalOpen}
        onClose={() => setCouponModalOpen(false)}
        onApply={handleApplyFromModal}
        appliedCode={appliedPromo}
        total={total}
      />
    </>
  );
}

export default function CheckoutPage({ cart, total, clearCart, user, appliedCoupon, setAppliedCoupon }) {
  const navigate = useNavigate();

  // PayU's checkout is a full-page redirect, so if the payment is cancelled/
  // fails and the customer is sent back to /checkout, every bit of React
  // state (including whatever they typed into the address form) is gone —
  // the page remounts from scratch. To avoid making them re-type everything,
  // we snapshot the form into sessionStorage right before redirecting to
  // PayU, and restore it here (once) if it's still there.
  const draftRef = useRef(undefined);
  if (draftRef.current === undefined) {
    try {
      const raw = sessionStorage.getItem(CHECKOUT_DRAFT_KEY);
      draftRef.current = raw ? JSON.parse(raw) : null;
    } catch {
      draftRef.current = null;
    }
    sessionStorage.removeItem(CHECKOUT_DRAFT_KEY);
  }
  const draft = draftRef.current;

  const [emailOffers, setEmailOffers] = useState(draft?.emailOffers ?? true);
  const [saveInfo, setSaveInfo] = useState(draft?.saveInfo ?? true);
  const [sameAsBilling, setSameAsBilling] = useState(draft?.sameAsBilling ?? true);
  const [payMethod, setPayMethod] = useState(draft?.payMethod || 'razorpay');
  const [promoCode, setPromoCode] = useState(draft?.promoCode || appliedCoupon?.code || '');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState(appliedCoupon ? `${appliedCoupon.label || appliedCoupon.code} applied!` : '');
  const [applying, setApplying] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!cart || cart.length === 0) return;
    trackEvent('InitiateCheckout', {
      content_ids: cart.map(i => String(i._id || i.id || '')),
      contents: cart.map(i => ({ id: String(i._id || i.id || ''), quantity: i.qty })),
      content_type: 'product',
      num_items: cart.reduce((s, i) => s + i.qty, 0),
      value: total || 0,
      currency: 'INR',
    });
    // Only ever fires once per checkout-page visit — cart edits made while
    // still on this page shouldn't re-fire it.
  }, []); // eslint-disable-line

  useEffect(() => {
    const payuError = searchParams.get('payuError');
    if (payuError) {
      setOrderError(payuError);
      const next = new URLSearchParams(searchParams);
      next.delete('payuError');
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line
  const [shippingSettings, setShippingSettings] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);

  useEffect(() => {
    getShippingSettings().then(setShippingSettings).catch(() => {});
    getPaymentSettings().then(setPaymentSettings).catch(() => {});
  }, []);
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  // Returning guest — prefill contact info remembered on this device the
  // last time they checked "Save this information for next time" without
  // an account, same idea as Shopify's guest-checkout autofill.
  const guestContactRef = useRef(undefined);
  if (guestContactRef.current === undefined) {
    guestContactRef.current = (!user && !draft) ? getGuestContact() : null;
  }
  const guestContact = guestContactRef.current;

  const [deliveryCoords, setDeliveryCoords] = useState(
    draft?.deliveryCoords ||
    ((!user && !draft && guestContact?.lat != null && guestContact?.lng != null)
      ? { lat: guestContact.lat, lng: guestContact.lng }
      : null)
  );
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(draft?.selectedAddressId || null);

  // Background-resolved approximate location — kept in sync with the address
  // fields as the customer types, so when they click "Select Delivery
  // Location" the modal can open with the pin already placed instead of
  // waiting on a geocode call.
  const [bgLocation, setBgLocation] = useState(null);
  const bgGeocodeRef    = useRef(null); // debounce timer
  const bgPollCancelRef = useRef(null); // cancel fn for the "wait for Maps" poll
  const bgQueryRef      = useRef('');   // always holds the latest built query

  const [billingForm, setBillingForm] = useState(draft?.billingForm || {
    firstName: '', lastName: '', address: '', apartment: '',
    city: '', state: '', pincode: '', phone: '',
  });
  const [billingErrors, setBillingErrors] = useState({});

  const [form, setForm] = useState(draft?.form || {
    email: user?.email || guestContact?.email || '',
    firstName: user?.firstName || guestContact?.firstName || '',
    lastName: user?.lastName || guestContact?.lastName || '',
    address: guestContact?.address || '',
    apartment: guestContact?.apartment || '',
    city: guestContact?.city || '',
    state: guestContact?.state || '',
    pincode: guestContact?.pincode || '',
    deliveryLocation: guestContact?.deliveryLocation || '',
    phone: user?.phone || guestContact?.phone || '',
  });

  // Load saved addresses when user is logged in
  useEffect(() => {
    // Signal to the server that this user reached the checkout page
    // (higher intent = higher-priority abandoned cart)
    if (user && cart.length > 0) {
      syncCart(cart, 'checkout').catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    getAddresses()
      .then(data => {
        if (!Array.isArray(data) || data.length === 0) return;
        setSavedAddresses(data);
        // If we just restored a draft (customer bounced back from PayU),
        // keep what they had instead of overwriting it with their default
        // saved address again.
        if (draft) return;
        // Auto-fill from default (or first) address
        const def = data.find(a => a.isDefault) || data[0];
        setSelectedAddressId(def._id);
        setForm(f => ({
          ...f,
          email: user.email || f.email,
          firstName: user.firstName || f.firstName,
          lastName: user.lastName || f.lastName,
          address: def.address || '',
          apartment: def.apartment || '',
          city: def.city || '',
          state: def.state || '',
          pincode: def.pincode || '',
          phone: def.phone || user.phone || '',
          deliveryLocation: def.deliveryLocation || '',
        }));
        if (def.lat != null && def.lng != null) {
          setDeliveryCoords({ lat: def.lat, lng: def.lng });
        }
      })
      .catch(() => {}); // silently ignore — guest checkout still works
  }, [user]); // eslint-disable-line

  // Sync local display state if coupon was applied in cart drawer
  useEffect(() => {
    if (appliedCoupon) {
      setPromoCode(appliedCoupon.code);
      setPromoSuccess(`${appliedCoupon.label || appliedCoupon.code} applied!`);
      setPromoError('');
    }
  }, [appliedCoupon]);

  // ── Auto-invalidate / recalculate coupon when cart total changes ──
  useEffect(() => {
    if (!appliedCoupon) return;
    const minOrder = appliedCoupon.minOrder || 0;
    if (minOrder > 0 && total < minOrder) {
      // Cart dropped below minOrder — clear coupon entirely
      setAppliedCoupon(null);
      setPromoCode('');
      setPromoSuccess('');
      setPromoError(`Coupon removed — minimum order ₹${minOrder} required.`);
      return;
    }
    // Recalculate discount for percentage-based coupons as total changes
    if (appliedCoupon.discountType === 'percentage' && appliedCoupon.discountValue) {
      const raw = (total * appliedCoupon.discountValue) / 100;
      const newDiscount = appliedCoupon.maxDiscount > 0
        ? Math.min(raw, appliedCoupon.maxDiscount)
        : raw;
      if (Math.round(newDiscount) !== Math.round(appliedCoupon.discountAmount || 0)) {
        setAppliedCoupon(prev => ({ ...prev, discountAmount: Math.round(newDiscount) }));
      }
    }
  }, [total]); // eslint-disable-line

  // ── Background geocoding of the address as the customer types ──
  // Debounced so we don't fire a geocode request on every keystroke. If the
  // Google Maps SDK hasn't finished loading yet, we wait for it instead of
  // giving up — otherwise a fast typist would never get a prefetch result.
  // The resolved location is handed to LocationPickerModal as
  // `prefetchedLocation`, so the modal can show the approximate pin the
  // instant it opens rather than waiting on a fresh geocode call.
  useEffect(() => {
    const query = buildQuery({
      address: form.address,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
    });

    bgQueryRef.current = query || '';

    if (bgGeocodeRef.current) clearTimeout(bgGeocodeRef.current);
    if (bgPollCancelRef.current) bgPollCancelRef.current();

    if (!query) {
      setBgLocation(null);
      return;
    }

    bgGeocodeRef.current = setTimeout(() => {
      bgPollCancelRef.current = waitForGoogleMaps(() => {
        // Re-read the ref instead of closing over `query` — if the customer
        // kept typing while we were waiting for Maps to load, this picks up
        // the latest address instead of geocoding a stale one.
        const latestQuery = bgQueryRef.current;
        if (!latestQuery) return;
        geocodeAddress(latestQuery).then(result => {
          if (result) setBgLocation(result);
        });
      });
    }, 600);

    return () => {
      if (bgGeocodeRef.current) clearTimeout(bgGeocodeRef.current);
      if (bgPollCancelRef.current) bgPollCancelRef.current();
    };
  }, [form.address, form.city, form.state, form.pincode]);

  // Fill form from a selected saved address
  const handleSelectSavedAddress = (addr) => {
    setSelectedAddressId(addr._id);
    setForm(f => ({
      ...f,
      address: addr.address || '',
      apartment: addr.apartment || '',
      city: addr.city || '',
      state: addr.state || '',
      pincode: addr.pincode || '',
      phone: addr.phone || '',
      deliveryLocation: addr.deliveryLocation || '',
    }));
    if (addr.lat != null && addr.lng != null) {
      setDeliveryCoords({ lat: addr.lat, lng: addr.lng });
    } else {
      setDeliveryCoords(null);
    }
    setErrors({});
  };

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  // Use discountAmount from the real coupon API response
  const discount = appliedCoupon
    ? (appliedCoupon.discountAmount || 0)
    : 0;
  // Use post-discount subtotal for the free-shipping threshold check so that
  // a coupon bringing the cart below the threshold correctly re-applies shipping,
  // and a cart qualifying only after discount correctly gets free shipping.
  const discountedSubtotal = Math.max(0, total - discount);
  const shippingResult = computeShipping(shippingSettings, { subtotal: discountedSubtotal, state: form.state, city: form.city, pincode: form.pincode });
  const shipping = shippingResult.amount ?? 0; // null while settings load
  const shippingLabel = shippingResult.label;
  const codCheck = codEligibility(shippingSettings, discountedSubtotal, { state: form.state, city: form.city, pincode: form.pincode });
  const codFee = (payMethod === 'cod' && shippingSettings) ? (shippingSettings.codFee || 0) : 0;
  const finalTotal = discountedSubtotal + shipping + codFee;

  useEffect(() => {
    if (payMethod === 'cod' && !codCheck.eligible) {
      setPayMethod('razorpay');
    }
  }, [codCheck.eligible]); // eslint-disable-line

  // If admin settings load and reveal the currently-selected gateway is
  // actually disabled, fall back to the first one that's still available.
  useEffect(() => {
    if (!paymentSettings) return;
    const isAvailable = (m) =>
      m === 'cod' ? codCheck.eligible :
      m === 'razorpay' ? !!paymentSettings.razorpay?.enabled :
      m === 'payu' ? !!paymentSettings.payu?.enabled :
      true;

    if (isAvailable(payMethod)) return;

    if (paymentSettings.razorpay?.enabled) setPayMethod('razorpay');
    else if (paymentSettings.payu?.enabled) setPayMethod('payu');
    else if (codCheck.eligible) setPayMethod('cod');
  }, [paymentSettings]); // eslint-disable-line

  // Keep local alias for template references
  const appliedPromo = appliedCoupon?.code || null;

  // Address fields that feed into the delivery location geocode/pin. If the
  // customer edits any of these after already confirming a delivery location
  // (either by picking a saved address or via the map picker), the old
  // pin is no longer guaranteed to match the new address — so we clear it
  // and make them reselect via the location picker rather than silently
  // keeping a stale location tied to the previous address text.
  const LOCATION_DEPENDENT_FIELDS = ['address', 'apartment', 'city', 'state', 'pincode'];

  const set = (field) => (e) => {
    const value = e.target.value;
    const clearsLocation = LOCATION_DEPENDENT_FIELDS.includes(field) && !!form.deliveryLocation;

    setForm(f => ({
      ...f,
      [field]: value,
      ...(clearsLocation ? { deliveryLocation: '' } : {}),
    }));
    if (clearsLocation) setDeliveryCoords(null);
    if (errors[field]) setErrors(er => ({ ...er, [field]: '' }));
  };

  // Guest cart sync — fires when the guest leaves the email field.
  // Only runs if: not logged in, email looks valid, cart has items.
  const handleEmailBlur = () => {
    if (user) return; // logged-in users are handled by App.js sync
    const email = form.email.trim();
    if (!/\S+@\S+\.\S+/.test(email)) return;
    if (!cart || cart.length === 0) return;
    guestSyncCart({
      items: cart,
      email,
      firstName: form.firstName || '',
      lastName:  form.lastName  || '',
    }).catch(() => {}); // silent — never disrupt UX
  };

  const setBilling = (field) => (e) => {
    setBillingForm(f => ({ ...f, [field]: e.target.value }));
    if (billingErrors[field]) setBillingErrors(er => ({ ...er, [field]: '' }));
  };

  const handleApplyPromo = async (codeOverride) => {
    const code = (codeOverride || promoCode).trim().toUpperCase();
    if (!code) return;
    setApplying(true);
    setPromoError('');
    setPromoSuccess('');
    try {
      const result = await validateCoupon(code, total);
      setAppliedCoupon({ ...result });
      setPromoCode(result.code);
      setPromoSuccess(`${result.label || result.code} applied!`);
    } catch (err) {
      setPromoError(err.message || 'Invalid coupon code.');
      setAppliedCoupon(null);
    } finally {
      setApplying(false);
    }
  };

  // Re-apply a coupon that was active before redirecting to PayU — the
  // coupon object itself lives in App.js state, which is also wiped by the
  // full-page navigation, so just restoring the promoCode text isn't enough.
  useEffect(() => {
    if (draft?.promoCode && !appliedCoupon) {
      handleApplyPromo(draft.promoCode);
    }
    // eslint-disable-next-line
  }, []);

  const handleRemovePromo = () => {
    setAppliedCoupon(null);
    setPromoCode('');
    setPromoSuccess('');
    setPromoError('');
  };

  const handleConfirmLocation = ({ address, lat, lng }) => {
    setForm(f => ({ ...f, deliveryLocation: address }));
    setDeliveryCoords({ lat, lng });
    if (errors.deliveryLocation) setErrors(er => ({ ...er, deliveryLocation: '' }));
    setLocationModalOpen(false);
  };

  const validate = () => {
    const e = {};
    if (!form.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email) && !/^\d{10}$/.test(form.email))
      e.email = 'Enter a valid email or 10-digit mobile number';
    if (!form.firstName) e.firstName = 'First name is required';
    if (!form.lastName)  e.lastName  = 'Last name is required';
    if (!form.address)   e.address   = 'Address is required';
    if (!form.city)      e.city      = 'City is required';
    if (!form.pincode || !/^\d{6}$/.test(form.pincode)) e.pincode = 'Enter a valid 6-digit PIN';
    if (!form.state) e.state = 'Please select a state';
    if (!form.deliveryLocation) e.deliveryLocation = 'Please select a delivery location';
    if (!form.phone || !/^\d{10}$/.test(form.phone)) e.phone = 'Enter a valid 10-digit phone number';

    // Validate billing address if using a different one
    if (!sameAsBilling) {
      const be = {};
      if (!billingForm.firstName) be.firstName = 'First name is required';
      if (!billingForm.lastName)  be.lastName  = 'Last name is required';
      if (!billingForm.address)   be.address   = 'Address is required';
      if (!billingForm.city)      be.city      = 'City is required';
      if (!billingForm.pincode || !/^\d{6}$/.test(billingForm.pincode)) be.pincode = 'Enter a valid 6-digit PIN';
      if (!billingForm.state) be.state = 'Please select a state';
      if (!billingForm.phone || !/^\d{10}$/.test(billingForm.phone)) be.phone = 'Enter a valid 10-digit phone number';
      setBillingErrors(be);
      if (Object.keys(be).length) e._billing = 'Billing address is incomplete';
    }

    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOrderError('');
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    if (payMethod === 'cod' && !codCheck.eligible) {
      setOrderError(codCheck.reason || 'Cash on Delivery is not available for this order.');
      return;
    }

    // ── Sync changes back to profile / addresses if user is logged in ──
    if (user) {
      const selectedAddr = savedAddresses.find(a => a._id === selectedAddressId);

      // 1. If the address came from a saved address, check if any fields changed
      if (selectedAddr) {
        const locationChanged = deliveryCoords && (
          selectedAddr.lat !== deliveryCoords.lat ||
          selectedAddr.lng !== deliveryCoords.lng ||
          selectedAddr.deliveryLocation !== form.deliveryLocation
        );
        const addressChanged =
          selectedAddr.address   !== form.address   ||
          selectedAddr.apartment !== form.apartment ||
          selectedAddr.city      !== form.city      ||
          selectedAddr.state     !== form.state     ||
          selectedAddr.pincode   !== form.pincode   ||
          selectedAddr.phone     !== form.phone     ||
          locationChanged;

        if (addressChanged) {
          try {
            const updated = await updateAddress(selectedAddr._id, {
              ...selectedAddr,
              address:   form.address,
              apartment: form.apartment,
              city:      form.city,
              state:     form.state,
              pincode:   form.pincode,
              phone:     form.phone,
              ...(deliveryCoords ? { lat: deliveryCoords.lat, lng: deliveryCoords.lng, deliveryLocation: form.deliveryLocation } : {}),
            });
            setSavedAddresses(prev => prev.map(a => a._id === selectedAddr._id ? updated : a));
          } catch { /* non-fatal — order still proceeds */ }
        }
      } else if (saveInfo) {
        // 2. No saved address was selected but user ticked "Save this information" — create new
        try {
          const saved = await addAddress({
            label: 'Home',
            address:   form.address,
            apartment: form.apartment,
            city:      form.city,
            state:     form.state,
            pincode:   form.pincode,
            phone:     form.phone,
            isDefault: savedAddresses.length === 0,
            ...(deliveryCoords ? { lat: deliveryCoords.lat, lng: deliveryCoords.lng, deliveryLocation: form.deliveryLocation } : {}),
          });
          setSavedAddresses(prev => [...prev, saved]);
          setSelectedAddressId(saved._id);
        } catch { /* non-fatal */ }
      }

      // 3. Sync first/last name/phone to profile if changed
      const profileChanged =
        user.firstName !== form.firstName ||
        user.lastName  !== form.lastName  ||
        (user.phone || '') !== form.phone;
      if (profileChanged) {
        try {
          await updateProfile({ firstName: form.firstName, lastName: form.lastName, phone: form.phone });
        } catch { /* non-fatal */ }
      }
    } else if (saveInfo) {
      // Guest checkout — remember contact + address on this device for next
      // time, since there's no account to save them to.
      saveGuestContact({
        email: form.email,
        phone: form.phone,
        firstName: form.firstName,
        lastName: form.lastName,
        address: form.address,
        apartment: form.apartment,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        deliveryLocation: form.deliveryLocation,
        ...(deliveryCoords ? { lat: deliveryCoords.lat, lng: deliveryCoords.lng } : {}),
      });
    } else if (!user) {
      // Guest unticked "save info" — forget anything previously remembered
      // on this device so stale data doesn't linger or get auto-filled.
      clearGuestContact();
    }

    const address = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      address: form.address,
      apartment: form.apartment,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      phone: form.phone,
    };

    const billingAddress = sameAsBilling ? null : {
      firstName: billingForm.firstName,
      lastName: billingForm.lastName,
      address: billingForm.address,
      apartment: billingForm.apartment,
      city: billingForm.city,
      state: billingForm.state,
      pincode: billingForm.pincode,
      phone: billingForm.phone,
    };

    const orderItems = cart.map(i => ({
      // Bundle-specific fields (undefined for regular products)
      type:                   i.type || 'product',
      bundleId:               i.bundleId               || undefined,
      bundleSelectedProducts: i.bundleSelectedProducts || undefined,
      id: i._id || i.id,
      name: i.name,
      price: i.price,
      qty: i.qty,
      image: i.imageUrl || i.image || '',
      selectedSize:    i._selectedSize    || '',
      selectedPlanter: i._selectedPlanter || '',
      selectedColor:   i._selectedColor   || '',
    }));

    const onOrderPlaced = (order) => {
      if (clearCart) clearCart();
      // Also clear the guest cart record on the server if this was a guest checkout
      if (!user && form.email) {
        guestSyncCart({ items: [], email: form.email }).catch(() => {});
      }
      navigate('/order-confirmation', { state: { order } });
    };

    if (payMethod === 'razorpay') {
      setSubmitting(true);
      try {
        await loadRazorpayScript();

        // Server recalculates the total from scratch — never trusts the client amount.
        const rzpOrder = await createRazorpayOrder({
          items: orderItems,
          address,
          billingAddress,
          promoCode: appliedCoupon?.code || undefined,
        });

        const rzp = new window.Razorpay({
          key: rzpOrder.keyId,
          order_id: rzpOrder.razorpayOrderId,
          amount: rzpOrder.amount,
          currency: rzpOrder.currency,
          name: 'Naturiq',
          description: `Order for ${cart.length} item${cart.length === 1 ? '' : 's'}`,
          prefill: {
            name: `${form.firstName} ${form.lastName}`.trim(),
            email: form.email,
            contact: form.phone,
          },
          theme: { color: '#2D6A4F' },
          handler: async (response) => {
            try {
              const order = await verifyRazorpayPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                items: orderItems,
                address,
                billingAddress,
                promoCode: appliedCoupon?.code || undefined,
                deliveryCoords: deliveryCoords || undefined,
              });
              onOrderPlaced(order);
            } catch (err) {
              setOrderError(err.message || 'Payment verification failed. Please contact support if money was deducted.');
            } finally {
              setSubmitting(false);
            }
          },
          modal: {
            // Fires when the user closes the Razorpay popup without paying
            ondismiss: () => setSubmitting(false),
          },
        });

        rzp.on('payment.failed', (resp) => {
          setOrderError(resp.error?.description || 'Payment failed. Please try again.');
          setSubmitting(false);
        });

        rzp.open();
      } catch (err) {
        setOrderError(err.message || 'Something went wrong starting payment. Please try again.');
        setSubmitting(false);
      }
      return;
    }

    if (payMethod === 'payu') {
      setSubmitting(true);
      try {
        // Server creates a pending Order and returns a signed form to post to
        // PayU's hosted checkout page. The browser leaves our site entirely;
        // PayU posts back to our server, which redirects to /order-confirmation.
        const payuRequest = await initiatePayuPayment({
          items: orderItems,
          address,
          billingAddress,
          promoCode: appliedCoupon?.code || undefined,
          deliveryCoords: deliveryCoords || undefined,
        });

        // Don't clear the cart yet — the customer hasn't paid until PayU
        // confirms it. If the payment fails, they're redirected back here
        // and should still see their cart/checkout exactly as they left it.
        // The cart is cleared on the confirmation page once PayU reports success.
        try {
          sessionStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify({
            form, billingForm, sameAsBilling, payMethod,
            deliveryCoords, selectedAddressId,
            promoCode: appliedCoupon?.code || promoCode || '',
            emailOffers, saveInfo,
          }));
        } catch { /* sessionStorage unavailable — non-fatal, just won't restore */ }

        redirectToPayu(payuRequest);
        // Note: no setSubmitting(false) here — the page is navigating away.
      } catch (err) {
        setOrderError(err.message || 'Something went wrong starting payment. Please try again.');
        setSubmitting(false);
      }
      return;
    }

    // Cash on Delivery — places the order directly, no payment gateway involved
    setSubmitting(true);
    try {
      const order = await placeOrder({
        items: orderItems,
        address,
        billingAddress,
        payment: 'cod',
        promoCode: appliedCoupon?.code || undefined,
        deliveryCoords: deliveryCoords || undefined,
      });
      onOrderPlaced(order);
    } catch (err) {
      setOrderError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const promoProps = { promoCode, setPromoCode, handleApplyPromo, promoError, promoSuccess, appliedPromo: appliedCoupon?.code || null, total, onRemove: handleRemovePromo, applying };

  if (cart.length === 0) {
    return (
      <main className="co-page">
        <div className="co-empty">
          <p>Your cart is empty.</p>
          <Link to="/collections/plants" className="co-empty__link">Browse Plants →</Link>
        </div>
      </main>
    );
  }

  const SummaryItems = () => (
    <div className="co-summary__items">
      {cart.map(item => (
        <div key={item.id} className="co-summary__item">
          <div className="co-summary__img-wrap">
            <span className="co-summary__badge">{item.qty}</span>
            <div className="co-summary__img">
              {(item.imageUrl || item.image)
                ? <img src={item.imageUrl || item.image} alt={item.name}
                    onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                : null}
              <div className="co-summary__img-fallback" style={(item.imageUrl || item.image) ? {display:'none'} : {}}>🌿</div>
            </div>
          </div>
          <div className="co-summary__item-body">
            <p className="co-summary__item-name">{item.name}</p>
            {(item._selectedSize || item._selectedPlanter || item._selectedColor) ? (
              <p className="co-summary__item-variant">
                {[item._selectedSize, item._selectedPlanter, item._selectedColor].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            {item.type === 'bundle' && item.bundleSelectedProducts?.length > 0 && (
              <div className="co-bundle-items">
                {item.bundleSelectedProducts.map((p, pi) => {
                  const variants = [p.sizeLabel, p.planterName, p.colorLabel].filter(Boolean).join(' · ');
                  return (
                    <div key={pi} className="co-bundle-item">
                      {p.imageUrl
                        ? <img src={p.imageUrl} alt={p.name} className="co-bundle-item__img" />
                        : <span className="co-bundle-item__fallback">🌿</span>
                      }
                      <span className="co-bundle-item__info">
                        <span className="co-bundle-item__name">{p.name}</span>
                        {variants && <span className="co-bundle-item__variant">{variants}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <p className="co-summary__item-price">₹{(item.price * item.qty).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );

  const SummaryLines = () => (
    <>
      <div className="co-summary__lines">
        <div className="co-summary__line">
          <span>Subtotal · {cartCount} {cartCount === 1 ? 'item' : 'items'}</span>
          <span>₹{total.toLocaleString()}</span>
        </div>
        {discount > 0 && (
          <div className="co-summary__line co-summary__line--disc">
            <span>Discount ({appliedPromo})</span>
            <span>−₹{discount.toLocaleString()}</span>
          </div>
        )}
        <div className="co-summary__line">
          <span>Shipping</span>
          <span className="co-summary__shipping">
            {form.address
              ? (shippingResult.loading ? <span className="co-shipping-loading">…</span> : shipping === 0 ? <span className="co-free">Free</span> : `₹${shipping}`)
              : 'Enter shipping address'}
          </span>
        </div>
        {codFee > 0 && (
          <div className="co-summary__line">
            <span>{shippingSettings?.codLabel ? `${shippingSettings.codLabel} fee` : 'Cash on Delivery fee'}</span>
            <span>₹{codFee.toLocaleString()}</span>
          </div>
        )}
      </div>
      <div className="co-divider" />
      <div className="co-summary__total">
        <div>
          <span className="co-summary__total-label">Total</span>
          <span className="co-summary__currency">INR</span>
        </div>
        <span className="co-summary__total-amount">₹{finalTotal.toLocaleString()}</span>
      </div>
    </>
  );

  return (
    <main className="co-page">
      <div className="co-inner">

        {/* ── Left column ── */}
        <div className="co-left">

          <form className="co-form" onSubmit={handleSubmit} noValidate>

            {/* Contact */}
            <section className="co-section">
              <div className="co-section__hd">
                <h2 className="co-section__title">Contact</h2>
              </div>

              <div className="co-field">
                <div className={`co-field-wrap${form.email ? ' is-filled' : ''}${errors.email ? ' is-err' : ''}`}>
                  <input
                    className={`co-input${errors.email ? ' co-input--err' : ''}${user ? ' co-input--readonly' : ''}`}
                    type="text"
                    placeholder=" "
                    value={form.email}
                    onChange={set('email')}
                    onBlur={handleEmailBlur}
                    readOnly={!!user}
                  />
                  <label className="co-float-label">Email address</label>
                </div>
                {errors.email && <p className="co-field__err">{errors.email}</p>}
              </div>

              <label className="co-checkbox">
                <input type="checkbox" checked={emailOffers} onChange={e => setEmailOffers(e.target.checked)} />
                <span className="co-checkbox__box">{emailOffers && <CheckIcon />}</span>
                <span className="co-checkbox__label">Email me with news and offers</span>
              </label>
            </section>

            {/* Delivery */}
            <section className="co-section">
              <h2 className="co-section__title">Delivery</h2>

              {/* Saved address switcher (logged-in users with ≥1 saved address) */}
              {user && savedAddresses.length > 0 && (
                <div className="co-saved-addrs">
                  <p className="co-saved-addrs__label">Saved addresses</p>
                  <div className="co-saved-addrs__list">
                    {savedAddresses.map(addr => (
                      <button
                        key={addr._id}
                        type="button"
                        className={`co-saved-addr-chip${selectedAddressId === addr._id ? ' co-saved-addr-chip--active' : ''}`}
                        onClick={() => handleSelectSavedAddress(addr)}
                      >
                        <span className="co-saved-addr-chip__label">{addr.label || 'Address'}</span>
                        <span className="co-saved-addr-chip__text">{addr.city}</span>
                        {addr.isDefault && <span className="co-saved-addr-chip__default">Default</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="co-field">
                <div className="co-select-wrap">
                  <select className="co-select" disabled>
                    <option>India</option>
                  </select>
                  <span className="co-select__label">Country / Region</span>
                </div>
              </div>

              <div className="co-row">
                <div className="co-field">
                  <div className={`co-field-wrap${form.firstName ? ' is-filled' : ''}${errors.firstName ? ' is-err' : ''}`}>
                    <input
                      className={`co-input${errors.firstName ? ' co-input--err' : ''}`}
                      type="text" placeholder=" "
                      value={form.firstName} onChange={set('firstName')}
                    />
                    <label className="co-float-label">First name</label>
                  </div>
                  {errors.firstName && <p className="co-field__err">{errors.firstName}</p>}
                </div>
                <div className="co-field">
                  <div className={`co-field-wrap${form.lastName ? ' is-filled' : ''}${errors.lastName ? ' is-err' : ''}`}>
                    <input
                      className={`co-input${errors.lastName ? ' co-input--err' : ''}`}
                      type="text" placeholder=" "
                      value={form.lastName} onChange={set('lastName')}
                    />
                    <label className="co-float-label">Last name</label>
                  </div>
                  {errors.lastName && <p className="co-field__err">{errors.lastName}</p>}
                </div>
              </div>

              <div className="co-field">
                <div className={`co-field-wrap${form.address ? ' is-filled' : ''}${errors.address ? ' is-err' : ''}`}>
                  <input
                    className={`co-input${errors.address ? ' co-input--err' : ''}`}
                    type="text" placeholder=" "
                    value={form.address} onChange={set('address')}
                  />
                  <label className="co-float-label">Address</label>
                </div>
                {errors.address && <p className="co-field__err">{errors.address}</p>}
              </div>

              <div className="co-field">
                <div className={`co-field-wrap${form.apartment ? ' is-filled' : ''}`}>
                  <input
                    className="co-input"
                    type="text" placeholder=" "
                    value={form.apartment} onChange={set('apartment')}
                  />
                  <label className="co-float-label">Apartment, suite, etc. (optional)</label>
                </div>
              </div>

              <div className="co-row co-row--3">
                <div className="co-field">
                  <div className={`co-field-wrap${form.city ? ' is-filled' : ''}${errors.city ? ' is-err' : ''}`}>
                    <input
                      className={`co-input${errors.city ? ' co-input--err' : ''}`}
                      type="text" placeholder=" "
                      value={form.city} onChange={set('city')}
                    />
                    <label className="co-float-label">City</label>
                  </div>
                  {errors.city && <p className="co-field__err">{errors.city}</p>}
                </div>
                <div className="co-field">
                  <div className="co-select-wrap">
                    <select className={`co-select${errors.state ? ' co-select--err' : ''}`} value={form.state} onChange={set('state')}>
                      <option value="">Select state</option>
                      {STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                    <span className="co-select__label">State</span>
                  </div>
                  {errors.state && <p className="co-field__err">{errors.state}</p>}
                </div>
                <div className="co-field">
                  <div className={`co-field-wrap${form.pincode ? ' is-filled' : ''}${errors.pincode ? ' is-err' : ''}`}>
                    <input
                      className={`co-input${errors.pincode ? ' co-input--err' : ''}`}
                      type="text" placeholder=" " maxLength={6}
                      value={form.pincode} onChange={set('pincode')}
                    />
                    <label className="co-float-label">PIN code</label>
                  </div>
                  {errors.pincode && <p className="co-field__err">{errors.pincode}</p>}
                </div>
              </div>

              <div className="co-field">
                {!form.deliveryLocation ? (
                  <button
                    type="button"
                    className={`co-location-trigger${errors.deliveryLocation ? ' co-location-trigger--err' : ''}`}
                    onClick={() => setLocationModalOpen(true)}
                  >
                    <span className="co-location-trigger__icon"><PinIcon /></span>
                    <span className="co-location-trigger__label">Select Delivery Location (For Fastest Delivery)</span>
                  </button>
                ) : (
                  <div className="co-location-card">
                    {deliveryCoords && <MapPreview lat={deliveryCoords.lat} lng={deliveryCoords.lng} />}
                    <div className="co-location-card__row">
                      <span className="co-location-card__icon"><PinIcon /></span>
                      <p className="co-location-card__address">{form.deliveryLocation}</p>
                      <button
                        type="button"
                        className="co-location-card__change"
                        onClick={() => setLocationModalOpen(true)}
                      >
                        Change
                      </button>
                    </div>
                  </div>
                )}
                {errors.deliveryLocation && <p className="co-field__err">{errors.deliveryLocation}</p>}
              </div>

              <div className="co-field">
                <div className={`co-field-wrap co-phone-wrap${form.phone ? ' is-filled' : ''}${errors.phone ? ' is-err' : ''}`}>
                  <input
                    className={`co-input${errors.phone ? ' co-input--err' : ''}`}
                    type="tel" placeholder=" " maxLength={10}
                    value={form.phone} onChange={set('phone')}
                  />
                  <label className="co-float-label">Phone</label>
                  <div className="co-phone-flag" aria-hidden="true">
                    <img className="co-phone-flag__img" src="/india-flag-svg.svg" alt="India" />
                  </div>
                </div>
                {errors.phone && <p className="co-field__err">{errors.phone}</p>}
              </div>

              <label className="co-checkbox">
                <input type="checkbox" checked={saveInfo} onChange={e => setSaveInfo(e.target.checked)} />
                <span className="co-checkbox__box">{saveInfo && <CheckIcon />}</span>
                <span className="co-checkbox__label">Save this information for next time</span>
              </label>
            </section>

            {/* Shipping method */}
            <section className="co-section">
              <h2 className="co-section__title">Shipping method</h2>
              <div className="co-shipping-placeholder">
                {form.address
                  ? (
                    <label className="co-radio-card co-radio-card--selected">
                      <span className="co-radio"><span className="co-radio__dot" /></span>
                      <span className="co-radio-card__body">
                        <span className="co-radio-card__name">
                          {shippingLabel}
                          {shippingResult.isLocalDelivery && shippingResult.localCityCutoff
                            ? ` · Order before ${shippingResult.localCityCutoff}`
                            : ''}
                          {shippingResult.minDays != null && shippingResult.maxDays != null
                            ? ` (${shippingResult.minDays === shippingResult.maxDays
                                ? `${shippingResult.minDays} day${shippingResult.minDays !== 1 ? 's' : ''}`
                                : `${shippingResult.minDays}–${shippingResult.maxDays} days`})`
                            : ''}
                          {shippingResult.isLocalDelivery && shippingResult.localCityNote
                            ? <span className="co-shipping-note">{shippingResult.localCityNote}</span>
                            : null}
                        </span>
                        <span className="co-radio-card__note">{shippingResult.description || 'Packed with care from our farm'}</span>
                      </span>
                      <span className="co-radio-card__price">
                        {shippingResult.loading ? <span className="co-shipping-loading">…</span> : shipping === 0 ? <span className="co-free">Free</span> : `₹${shipping}`}
                      </span>
                    </label>
                  )
                  : <p className="co-placeholder-text">Enter your shipping address to view available shipping methods.</p>
                }
              </div>
            </section>

            {/* Payment */}
            <section className="co-section">
              <h2 className="co-section__title">Payment</h2>
              <p className="co-section__sub">
                <ShieldIcon /> All transactions are secure and encrypted.
              </p>

              <div className="co-payment-options">
                {(paymentSettings === null || paymentSettings?.razorpay?.enabled) && (
                  <>
                    <label className={`co-radio-card${payMethod === 'razorpay' ? ' co-radio-card--selected' : ''}`}
                      onClick={() => setPayMethod('razorpay')}>
                      <span className="co-radio">{payMethod === 'razorpay' && <span className="co-radio__dot" />}</span>
                      <span className="co-radio-card__body">
                        <span className="co-radio-card__name">Razorpay Secure (UPI, Cards, Int'l Cards, Wallets)</span>
                      </span>
                      <span className="co-payment-icons">
                        <img className="co-pi-img" src="/payment-icons/upi.svg" alt="UPI" title="UPI" />
                        <img className="co-pi-img" src="/payment-icons/visa.svg" alt="Visa" title="Visa" />
                        <img className="co-pi-img" src="/payment-icons/mastercard.svg" alt="Mastercard" title="Mastercard" />
                        <img className="co-pi-img co-pi-img--hide-sm" src="/payment-icons/rupay.svg" alt="RuPay" title="RuPay" />
                        <span className="co-pi co-pi--more">+17</span>
                      </span>
                    </label>

                    {payMethod === 'razorpay' && (
                      <div className="co-payment-info">
                        You'll be redirected to Razorpay Secure (UPI, Cards, Int'l Cards, Wallets) to complete your purchase.
                      </div>
                    )}
                  </>
                )}

                {paymentSettings?.payu?.enabled && (
                  <>
                    <label className={`co-radio-card${payMethod === 'payu' ? ' co-radio-card--selected' : ''}`}
                      onClick={() => setPayMethod('payu')}>
                      <span className="co-radio">{payMethod === 'payu' && <span className="co-radio__dot" />}</span>
                      <span className="co-radio-card__body">
                        <span className="co-radio-card__name">PayU (UPI, Cards, Net Banking, Wallets)</span>
                      </span>
                      <span className="co-payment-icons">
                        <img className="co-pi-img" src="/payment-icons/upi.svg" alt="UPI" title="UPI" />
                        <img className="co-pi-img" src="/payment-icons/visa.svg" alt="Visa" title="Visa" />
                        <img className="co-pi-img" src="/payment-icons/mastercard.svg" alt="Mastercard" title="Mastercard" />
                      </span>
                    </label>

                    {payMethod === 'payu' && (
                      <div className="co-payment-info">
                        You'll be redirected to PayU's secure checkout page to complete your purchase.
                      </div>
                    )}
                  </>
                )}

                <label
                  className={`co-radio-card${payMethod === 'cod' ? ' co-radio-card--selected' : ''}${!codCheck.eligible ? ' co-radio-card--disabled' : ''}`}
                  aria-disabled={!codCheck.eligible}
                  onClick={() => { if (codCheck.eligible) setPayMethod('cod'); }}
                >
                  <span className="co-radio">{payMethod === 'cod' && <span className="co-radio__dot" />}</span>
                  <span className="co-radio-card__body">
                    <span className="co-radio-card__name">{shippingSettings?.codLabel || 'Cash on Delivery'}</span>
                    <span className="co-radio-card__note">
                      {codCheck.eligible
                        ? (shippingSettings?.codDescription
                            ? `${shippingSettings.codDescription}${shippingSettings?.codFee ? ` (₹${shippingSettings.codFee} fee applies)` : ''}`
                            : `Pay with cash when your order arrives${shippingSettings?.codFee ? ` (₹${shippingSettings.codFee} fee applies)` : ''}`)
                        : codCheck.reason}
                    </span>
                  </span>
                </label>
              </div>
            </section>

            {/* Billing address */}
            <section className="co-section">
              <h2 className="co-section__title">Billing address</h2>
              <div className="co-payment-options">
                <label className={`co-radio-card${sameAsBilling ? ' co-radio-card--selected' : ''}`}
                  onClick={() => { setSameAsBilling(true); setBillingErrors({}); }}>
                  <span className="co-radio">{sameAsBilling && <span className="co-radio__dot" />}</span>
                  <span className="co-radio-card__body">
                    <span className="co-radio-card__name">Same as shipping address</span>
                  </span>
                </label>
                <label className={`co-radio-card${!sameAsBilling ? ' co-radio-card--selected' : ''}`}
                  onClick={() => setSameAsBilling(false)}>
                  <span className="co-radio">{!sameAsBilling && <span className="co-radio__dot" />}</span>
                  <span className="co-radio-card__body">
                    <span className="co-radio-card__name">Use a different billing address</span>
                  </span>
                </label>
              </div>

              {/* Billing address form — expands when user picks a different billing address */}
              {!sameAsBilling && (
                <div className="co-billing-form">
                  <div className="co-field">
                    <div className="co-select-wrap">
                      <select className="co-select" disabled>
                        <option>India</option>
                      </select>
                      <span className="co-select__label">Country / Region</span>
                    </div>
                  </div>

                  <div className="co-row">
                    <div className="co-field">
                      <div className={`co-field-wrap${billingForm.firstName ? ' is-filled' : ''}${billingErrors.firstName ? ' is-err' : ''}`}>
                        <input
                          className={`co-input${billingErrors.firstName ? ' co-input--err' : ''}`}
                          type="text" placeholder=" "
                          value={billingForm.firstName} onChange={setBilling('firstName')}
                        />
                        <label className="co-float-label">First name</label>
                      </div>
                      {billingErrors.firstName && <p className="co-field__err">{billingErrors.firstName}</p>}
                    </div>
                    <div className="co-field">
                      <div className={`co-field-wrap${billingForm.lastName ? ' is-filled' : ''}${billingErrors.lastName ? ' is-err' : ''}`}>
                        <input
                          className={`co-input${billingErrors.lastName ? ' co-input--err' : ''}`}
                          type="text" placeholder=" "
                          value={billingForm.lastName} onChange={setBilling('lastName')}
                        />
                        <label className="co-float-label">Last name</label>
                      </div>
                      {billingErrors.lastName && <p className="co-field__err">{billingErrors.lastName}</p>}
                    </div>
                  </div>

                  <div className="co-field">
                    <div className={`co-field-wrap${billingForm.address ? ' is-filled' : ''}${billingErrors.address ? ' is-err' : ''}`}>
                      <input
                        className={`co-input${billingErrors.address ? ' co-input--err' : ''}`}
                        type="text" placeholder=" "
                        value={billingForm.address} onChange={setBilling('address')}
                      />
                      <label className="co-float-label">Address</label>
                    </div>
                    {billingErrors.address && <p className="co-field__err">{billingErrors.address}</p>}
                  </div>

                  <div className="co-field">
                    <div className={`co-field-wrap${billingForm.apartment ? ' is-filled' : ''}`}>
                      <input
                        className="co-input"
                        type="text" placeholder=" "
                        value={billingForm.apartment} onChange={setBilling('apartment')}
                      />
                      <label className="co-float-label">Apartment, suite, etc. (optional)</label>
                    </div>
                  </div>

                  <div className="co-row co-row--3">
                    <div className="co-field">
                      <div className={`co-field-wrap${billingForm.city ? ' is-filled' : ''}${billingErrors.city ? ' is-err' : ''}`}>
                        <input
                          className={`co-input${billingErrors.city ? ' co-input--err' : ''}`}
                          type="text" placeholder=" "
                          value={billingForm.city} onChange={setBilling('city')}
                        />
                        <label className="co-float-label">City</label>
                      </div>
                      {billingErrors.city && <p className="co-field__err">{billingErrors.city}</p>}
                    </div>
                    <div className="co-field">
                      <div className="co-select-wrap">
                        <select className={`co-select${billingErrors.state ? ' co-select--err' : ''}`} value={billingForm.state} onChange={setBilling('state')}>
                          <option value="">Select state</option>
                          {STATES.map(s => <option key={s}>{s}</option>)}
                        </select>
                        <span className="co-select__label">State</span>
                      </div>
                      {billingErrors.state && <p className="co-field__err">{billingErrors.state}</p>}
                    </div>
                    <div className="co-field">
                      <div className={`co-field-wrap${billingForm.pincode ? ' is-filled' : ''}${billingErrors.pincode ? ' is-err' : ''}`}>
                        <input
                          className={`co-input${billingErrors.pincode ? ' co-input--err' : ''}`}
                          type="text" placeholder=" " maxLength={6}
                          value={billingForm.pincode} onChange={setBilling('pincode')}
                        />
                        <label className="co-float-label">PIN code</label>
                      </div>
                      {billingErrors.pincode && <p className="co-field__err">{billingErrors.pincode}</p>}
                    </div>
                  </div>

                  <div className="co-field">
                    <div className={`co-field-wrap co-phone-wrap${billingForm.phone ? ' is-filled' : ''}${billingErrors.phone ? ' is-err' : ''}`}>
                      <input
                        className={`co-input${billingErrors.phone ? ' co-input--err' : ''}`}
                        type="tel" placeholder=" " maxLength={10}
                        value={billingForm.phone} onChange={setBilling('phone')}
                      />
                      <label className="co-float-label">Phone</label>
                      <div className="co-phone-flag" aria-hidden="true">
                        <img className="co-phone-flag__img" src="/india-flag-svg.svg" alt="India" />
                      </div>
                    </div>
                    {billingErrors.phone && <p className="co-field__err">{billingErrors.phone}</p>}
                  </div>
                </div>
              )}
            </section>

            {/* ── Mobile-only order summary ── */}
            <div className="co-mobile-summary">
              <div className="co-divider" />

              {/* Coupon section ABOVE order summary on mobile */}
              <div className="co-mobile-coupon">
                <h3 className="co-mobile-coupon__title">Discount Code</h3>
                <PromoSection {...promoProps} />
              </div>

              <div className="co-divider" />

              {/* Order summary items */}
              <SummaryItems />
              <div className="co-divider" />
              <SummaryLines />
              <div className="co-divider" />
            </div>

            {/* Submit */}
            {orderError && <p className="co-field__err" style={{ marginBottom: '12px', textAlign: 'center' }}>{orderError}</p>}
            <button type="submit" className={`co-pay-btn${submitting ? ' co-pay-btn--loading' : ''}`} disabled={submitting}>
              {submitting ? 'Processing…' : (payMethod === 'cod' ? 'Place order' : 'Pay now')}
            </button>

            <div className="co-footer-links">
              <Link to="/">Refund policy</Link>
              <Link to="/">Shipping policy</Link>
              <Link to="/">Privacy policy</Link>
              <Link to="/">Terms of service</Link>
            </div>
          </form>
        </div>

        {/* ── Right column / Order summary ── */}
        <aside className="co-right">
          <div className="co-summary">

            {/* Items */}
            <SummaryItems />

            <div className="co-divider" />

            {/* Discount code with View All Coupons */}
            <PromoSection {...promoProps} />

            <div className="co-divider" />

            {/* Totals */}
            <SummaryLines />

          </div>
        </aside>

      </div>

      <LocationPickerModal
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        onConfirm={handleConfirmLocation}
        initialLocation={deliveryCoords ? { ...deliveryCoords, address: form.deliveryLocation } : null}
        prefetchedLocation={bgLocation}
        formAddress={{
          address: form.address,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
        }}
      />
    </main>
  );
}